import { useState } from "react";
import { Link2, Loader2 } from "lucide-react";
import { useDataProvider, useListContext, useNotify } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Contact } from "../types";
import type { CrmDataProvider } from "../providers/supabase/dataProvider";
import { contactName } from "./contactName";

// Loops' contact API is one-request-per-contact and rate-limited to ~10/s per
// team, so we link in small throttled chunks rather than one big burst (which
// silently drops ~10% to 429s). Already-linked contacts are excluded up front,
// making the action idempotent — re-running only picks up what's still missing.
const CHUNK_SIZE = 5;
const CHUNK_DELAY_MS = 600; // ~5 requests / 600ms ≈ 8/s, safely under 10/s
const MAX_CONTACTS = 5000; // whole audience is ~2,700; headroom for growth

// Bulk-link every contact not yet in Loops (within the current filtered view).
//
// The `includeLinked` mode also re-pushes contacts that are already linked. That
// is the backfill path: company only started syncing on 2026-07-21, and the link
// path omitted it entirely before that, so contacts linked earlier sit in Loops
// with no company. loops_link upserts by email and writes the id back, so
// re-running it on an already-linked contact is idempotent — it just refreshes
// the payload, company included.
export const LinkAllToLoopsButton = () => {
  const { filterValues } = useListContext();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [done, setDone] = useState(0);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [includeLinked, setIncludeLinked] = useState(false);

  const fetchContacts = async (withLinked: boolean) => {
    const { data } = await dataProvider.getList<Contact>("contacts", {
      // Respect the current view's filters. By default constrain to
      // not-yet-linked; the backfill mode drops that constraint.
      filter: withLinked
        ? { ...filterValues }
        : { ...filterValues, "loops_contact_id@is": "null" },
      pagination: { page: 1, perPage: MAX_CONTACTS },
      sort: { field: "last_seen", order: "DESC" },
    });
    // Loops matches on email, so only email-bearing contacts can be linked.
    return data.filter((c) => c.email);
  };

  const openDialog = async () => {
    setLoading(true);
    try {
      const linkable = await fetchContacts(false);
      setIncludeLinked(false);
      setContacts(linkable);
      if (linkable.length === 0) {
        notify("crm.loops.none_to_link", { type: "info" });
        return;
      }
      setDone(0);
      setOpen(true);
    } catch {
      notify("crm.loops.link_failed", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const toggleIncludeLinked = async (next: boolean) => {
    setLoading(true);
    try {
      setContacts(await fetchContacts(next));
      setIncludeLinked(next);
      setDone(0);
    } catch {
      notify("crm.loops.link_failed", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleLinkAll = async () => {
    setLinking(true);
    setDone(0);
    let ok = 0;
    let failed = 0;
    for (let i = 0; i < contacts.length; i += CHUNK_SIZE) {
      const chunk = contacts.slice(i, i + CHUNK_SIZE);
      const results = await Promise.allSettled(
        chunk.map((c) => dataProvider.linkToLoops(c.id)),
      );
      ok += results.filter((r) => r.status === "fulfilled").length;
      failed += results.filter((r) => r.status === "rejected").length;
      setDone(Math.min(i + chunk.length, contacts.length));
      if (i + CHUNK_SIZE < contacts.length) {
        await new Promise((resolve) => setTimeout(resolve, CHUNK_DELAY_MS));
      }
    }
    if (failed === 0) {
      notify("crm.loops.linked_count", {
        type: "success",
        messageArgs: { count: ok },
      });
    } else {
      notify("crm.loops.link_partial", {
        type: "warning",
        messageArgs: { ok, failed },
      });
    }
    queryClient.invalidateQueries({ queryKey: ["contacts"] });
    setLinking(false);
    setOpen(false);
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-9 cursor-pointer"
        disabled={loading}
        onClick={openDialog}
      >
        {loading ? (
          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
        ) : (
          <Link2 className="w-4 h-4 mr-1" />
        )}
        Link all to Loops
      </Button>
      <Dialog open={open} onOpenChange={(next) => !linking && setOpen(next)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link to Loops</DialogTitle>
            <DialogDescription>
              {includeLinked ? (
                <>
                  Sync {contacts.length} contact
                  {contacts.length === 1 ? "" : "s"} to Loops, including those
                  already linked. This is throttled to Loops' rate limit — keep
                  this tab open until it finishes.
                </>
              ) : (
                <>
                  Link {contacts.length} contact
                  {contacts.length === 1 ? "" : "s"} not yet in Loops. This is
                  throttled to Loops' rate limit, so it may take a moment.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 cursor-pointer"
              checked={includeLinked}
              disabled={linking || loading}
              onChange={(e) => toggleIncludeLinked(e.target.checked)}
            />
            <span>
              Also re-sync contacts already linked
              <span className="block text-xs text-muted-foreground">
                Updates their company in Loops. Back up Loops before running
                this across the whole audience.
              </span>
            </span>
          </label>
          {/* Cap the viewport height (not the root) so long lists scroll. */}
          <ScrollArea className="rounded-md border [&_[data-slot=scroll-area-viewport]]:max-h-60">
            <ul className="divide-y text-sm">
              {contacts.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center justify-between gap-3 px-3 py-2"
                >
                  <span className="min-w-0 flex-1 truncate">
                    {contactName(c)}
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground truncate max-w-[45%]">
                    {c.email}
                  </span>
                </li>
              ))}
            </ul>
          </ScrollArea>
          <DialogFooter>
            <DialogClose asChild>
              <Button
                variant="ghost"
                disabled={linking}
                className="cursor-pointer"
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              onClick={handleLinkAll}
              disabled={linking || loading || contacts.length === 0}
              className="cursor-pointer"
            >
              {linking || loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : null}
              {linking
                ? `${includeLinked ? "Syncing" : "Linking"} ${done}/${contacts.length}`
                : `${includeLinked ? "Sync" : "Link"} ${contacts.length}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
